import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Button,
  Stack,
  Card,
  Title,
  Group,
  Divider,
  ColorInput,
  Box,
  Text,
  SimpleGrid,
} from "@mantine/core";
import { DateTimePicker, NumberInput, Switch } from "react-hook-form-mantine";

import {
  useGetRegistrationSettings,
  useUpdateRegistrationSettings,
} from "../../api/registrations";
import resolver from "@congrs/resolver";
import { web_registrationSettingsSchema } from "@congrs/schemas";
import { extractDirtyData } from "../../utils/dirtyData";
import { colorSwatches } from "../Events/Customize/utils/colorUtils";
import SessionDescriptionEditor from "../Sessions/SessionDescriptionEditor";
import { mergeI18nWithDefaults } from "../../utils/i18nUtils";
import { Tabs } from "@mantine/core";
import CopyableUrl from "../CopyableUrl";

const SUBMISSION_COPY_RECIPIENTS = [
  {
    value: "registration",
    label: "Registration email",
    description:
      "A single email with the full registration summary is sent to the Billing Information email address.",
  },
  {
    value: "attendee",
    label: "Each attendee",
    description:
      "Each attendee receives an email with only their own ticket details.",
  },
];

function Settings({ event }) {
  const { data, isLoading } = useGetRegistrationSettings(event.id);
  const updateSettings = useUpdateRegistrationSettings(event.id);

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];
  const allLanguages = [mainLanguage, ...otherLanguages];

  const registrationUrl = `${window.location.origin}/events/${event.slug || event.id}/register`;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty, dirtyFields, errors },
  } = useForm({
    defaultValues: {
      registrationsOpenDate: null,
      registrationsCloseDate: null,
      maxQuantity: 10,
      maxCapacity: null,
      color: null,
      displaySponsors: false,
      sendSubmissionCopyEmail: false,
      submissionCopyRecipients: ["registration"],
      customTextI18n: Object.fromEntries(allLanguages.map((l) => [l, null])),
    },
    resolver: resolver(web_registrationSettingsSchema),
  });

  useEffect(() => {
    if (data) {
      reset({
        registrationsOpenDate: data.registrationsOpenDate ?? null,
        registrationsCloseDate: data.registrationsCloseDate ?? null,
        maxQuantity: data.maxQuantity ?? 10,
        maxCapacity: data.maxCapacity ?? null,
        color: data.color ?? null,
        displaySponsors: data.displaySponsors ?? true,
        sendSubmissionCopyEmail: data.sendSubmissionCopyEmail ?? false,
        submissionCopyRecipients: data.submissionCopyRecipients?.length
          ? data.submissionCopyRecipients
          : ["registration"],
        customTextI18n: mergeI18nWithDefaults(
          data.customTextI18n ?? {},
          mainLanguage,
          otherLanguages
        ),
      });
    }
  }, [data, reset]);

  const recipients = watch("submissionCopyRecipients") ?? [];

  const toggleRecipient = (value) => {
    const next = recipients.includes(value)
      ? recipients.filter((recipient) => recipient !== value)
      : [...recipients, value];

    const ordered = SUBMISSION_COPY_RECIPIENTS.map(
      (option) => option.value
    ).filter((option) => next.includes(option));

    setValue(
      "submissionCopyRecipients",
      // Deselecting the last option selects the other one instead, so the
      // selection is never empty while the email is enabled.
      ordered.length
        ? ordered
        : [
            SUBMISSION_COPY_RECIPIENTS.find((option) => option.value !== value)
              .value,
          ],
      { shouldDirty: true }
    );
  };

  const onSubmit = (values) => {
    const payload = extractDirtyData(values, dirtyFields);

    if (payload.registrationsOpenDate)
      payload.registrationsOpenDate = new Date(
        payload.registrationsOpenDate
      ).toISOString();
    if (payload.registrationsCloseDate)
      payload.registrationsCloseDate = new Date(
        payload.registrationsCloseDate
      ).toISOString();

    updateSettings.mutate(payload, {
      onSuccess: () => reset(values),
    });
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Card
      shadow="sm"
      p="lg"
      radius="md"
      mt="sm"
      withBorder
      style={{ maxWidth: 600 }}
    >
      <Title order={3} mb="md">
        Registration Form Settings
      </Title>

      {/* Registration URL Section */}
      <Box mb="lg">
        <Text size="sm" fw={500} mb="xs">
          Registration Page URL
        </Text>
        <CopyableUrl url={registrationUrl} />
      </Box>

      <Divider mb="lg" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          {/* Appearance */}
          <Box>
            <Title order={5} mb="md">
              Appearance
            </Title>
            <Stack gap="md">
              <ColorInput
                label="Accent color"
                placeholder="Pick a color"
                value={watch("color") || ""}
                onChange={(color) =>
                  setValue("color", color || null, { shouldDirty: true })
                }
                format="hex"
                withEyeDropper={false}
                withPicker
                swatches={colorSwatches}
                swatchesPerRow={6}
                disallowInput={false}
                closeOnColorSwatchClick
                error={errors.color?.message}
              />

              <Switch
                control={control}
                name="displaySponsors"
                label="Display sponsors on registration page"
                color="blue"
              />
            </Stack>
          </Box>

          <Divider />

          {/* Custom Text */}
          <Box>
            <Title order={5} mb="md">
              Custom Text
            </Title>
            <Tabs defaultValue={mainLanguage}>
              <Tabs.List mb="md">
                {allLanguages.map((lang) => (
                  <Tabs.Tab key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </Tabs.Tab>
                ))}
              </Tabs.List>

              {allLanguages.map((lang) => (
                <Tabs.Panel key={lang} value={lang}>
                  <Controller
                    name={`customTextI18n.${lang}`}
                    control={control}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <SessionDescriptionEditor
                        onChange={onChange}
                        content={value}
                        error={error}
                      />
                    )}
                  />
                </Tabs.Panel>
              ))}
            </Tabs>
          </Box>

          <Divider />

          {/* Registration Period */}
          <Box>
            <Title order={5} mb="md">
              Registration Period
            </Title>
            <Stack gap="md">
              <DateTimePicker
                control={control}
                name="registrationsOpenDate"
                label="Opens"
                placeholder="Select date and time"
                value={watch("registrationsOpenDate") ?? null}
                onChange={(date) =>
                  setValue("registrationsOpenDate", date ?? null, {
                    shouldDirty: true,
                  })
                }
                error={errors.registrationsOpenDate?.message}
              />

              <DateTimePicker
                control={control}
                name="registrationsCloseDate"
                label="Closes"
                placeholder="Select date and time"
                value={watch("registrationsCloseDate") ?? null}
                onChange={(date) =>
                  setValue("registrationsCloseDate", date ?? null, {
                    shouldDirty: true,
                  })
                }
                error={errors.registrationsCloseDate?.message}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Capacity Limits */}
          <Box>
            <Title order={5} mb="md">
              Capacity Limits
            </Title>
            <Stack gap="md">
              <NumberInput
                control={control}
                name="maxQuantity"
                label="Max tickets per registration"
                min={1}
                error={errors.maxQuantity?.message}
              />

              <NumberInput
                control={control}
                name="maxCapacity"
                label="Event capacity"
                placeholder="Unlimited"
                min={1}
                error={errors.maxCapacity?.message}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Submission copy email */}
          <Box>
            <Group align="center" wrap="nowrap" gap="sm" mb="md">
              <Title order={5}>Successful Registration Email</Title>
              <Switch
                control={control}
                name="sendSubmissionCopyEmail"
                color="blue"
              />
            </Group>

            {watch("sendSubmissionCopyEmail") && (
              <>
                <Text size="sm" fw={500} mb="xs">
                  Send to
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  {SUBMISSION_COPY_RECIPIENTS.map(
                    ({ value, label, description }) => {
                      const selected = recipients.includes(value);
                      return (
                        <Card
                          key={value}
                          withBorder
                          shadow="sm"
                          padding="md"
                          radius="md"
                          role="checkbox"
                          aria-checked={selected}
                          tabIndex={0}
                          onClick={() => toggleRecipient(value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleRecipient(value);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            borderColor: selected
                              ? "var(--mantine-color-blue-5)"
                              : undefined,
                          }}
                        >
                          <Stack gap="xs">
                            <Text fw={600}>{label}</Text>
                            <Text size="sm" c="dimmed">
                              {description}
                            </Text>
                          </Stack>
                        </Card>
                      );
                    }
                  )}
                </SimpleGrid>
              </>
            )}
          </Box>

          <Group justify="flex-end" mt="md">
            <Button
              type="submit"
              loading={updateSettings.isPending}
              disabled={!isDirty}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

export default Settings;
